import { Ban } from 'lucide-react';
import { useState } from 'react';
import {
  AutocompleteFilter,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useReportFilterOptions } from '../../hooks';
import type { ReportExplorerFilter } from '../../model';
import {
  reportFieldFilters,
  reportFilterDefinitions,
  reportFilterModes,
  setReportFieldFilter,
} from '../../reportFilters';
import {
  ReportExplorerScope,
  ReportFilterLookupMode,
  ReportFilterMode,
  type ReportFilterField,
} from '../../reportEnums';

interface ReportDimensionFilterProps {
  field: ReportFilterField;
  filters: readonly ReportExplorerFilter[];
  disabled: boolean;
  scope?: ReportExplorerScope | undefined;
  publisherId?: string | undefined;
  onChange: (filters: ReportExplorerFilter[]) => void;
}

/** Inline value filter for one selected dimension; every change applies immediately. */
export function ReportDimensionFilter({
  field,
  filters,
  disabled,
  scope = ReportExplorerScope.Standard,
  publisherId,
  onChange,
}: ReportDimensionFilterProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [draftMode, setDraftMode] = useState(ReportFilterMode.Include);
  const [opened, setOpened] = useState(false);
  const definition = reportFilterDefinitions[field];
  const fieldLabel = dashboardT(definition.labelKey);
  const active = reportFieldFilters(filters, field)[0];
  const mode = active?.mode ?? draftMode;
  const selectedValues = active?.values ?? [];
  const query = useReportFilterOptions(field, searchTerm, opened && !disabled, scope, publisherId);
  const serverSearch = definition.lookupMode === ReportFilterLookupMode.Server;
  const options = [
    ...new Map(
      [...selectedValues, ...(query.data ?? [])].map((option) => [option.id, option]),
    ).values(),
  ];
  const excludeSupported = reportFilterModes(field).includes(ReportFilterMode.Exclude);
  const excluding = mode === ReportFilterMode.Exclude;

  function changeMode(nextMode: ReportFilterMode) {
    setDraftMode(nextMode);
    if (selectedValues.length) {
      onChange(setReportFieldFilter(filters, field, nextMode, selectedValues));
    }
  }

  return (
    <div className="flex min-w-0 items-center gap-1 font-normal">
      <AutocompleteFilter
        label={dashboardT('reportExplorer.filters.valuesFor', { field: fieldLabel })}
        placeholder={dashboardT('reportExplorer.filters.allValues')}
        className="h-8 min-w-0 flex-1 px-2 text-xs"
        options={options}
        selectedIds={selectedValues.map(({ id }) => id)}
        disabled={disabled}
        loading={query.isFetching}
        loadingPlaceholder=""
        searchPlaceholder={dashboardT('reportExplorer.filters.searchValues', { field: fieldLabel })}
        emptyMessage={dashboardT(
          query.isError ? 'reportExplorer.filters.lookupError' : 'reportExplorer.filters.noValues',
        )}
        searchDebounceMs={serverSearch ? 650 : 0}
        onSearchTermChange={serverSearch ? setSearchTerm : undefined}
        onOpenChange={(open) => {
          if (open) setOpened(true);
        }}
        onSelectedIdsChange={(ids) => {
          const values = ids.flatMap((id) => {
            const option = options.find((candidate) => candidate.id === id);
            return option ? [option] : [];
          });
          onChange(setReportFieldFilter(filters, field, mode, values));
        }}
      />
      {excludeSupported ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            {/* Tooltip owns `data-state` on this element, so pressed styles key off aria-pressed. */}
            <TooltipTrigger asChild>
              <Toggle
                size="sm"
                className="text-muted-foreground aria-pressed:bg-destructive/10 aria-pressed:text-destructive aria-pressed:hover:bg-destructive/15 aria-pressed:hover:text-destructive h-8 w-8 shrink-0 p-0"
                pressed={excluding}
                disabled={disabled}
                aria-label={dashboardT('reportExplorer.filters.excludeFor', { field: fieldLabel })}
                onPressedChange={(pressed) => {
                  changeMode(pressed ? ReportFilterMode.Exclude : ReportFilterMode.Include);
                }}
              >
                <Ban aria-hidden="true" />
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>
              {dashboardT('reportExplorer.filters.excludeFor', { field: fieldLabel })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}
